import {
  Dialog as MUIDialog,
  DialogProps as MUIDialogProps,
} from '@mui/material';
import { useCallback, useEffect, useRef } from 'react';

// we implement custom handling for escape keys
type RelevantMUIDialogProps = Omit<MUIDialogProps, 'disableEscapeKeyDown'>;

export interface DialogProps extends RelevantMUIDialogProps {
  onClose?: () => void;
  /**
   * If true, clicking outside the modal (backdrop click) will not close the modal.
   * Users must explicitly click a button or the X to close.
   */
  disableBackdropClick?: boolean;
}

export function Dialog({
  open,
  onClose,
  disableBackdropClick,
  ...props
}: DialogProps) {
  const nodeRef = useRef<HTMLDivElement | null>(null);

  // we have this custom handling for escape rather than using the native MUIDialog escape key handling because
  // we have a fair number of nested modals and we only want to close the *top-most* modal; the native MUIDialog
  // handling closes *all open modals* on escape.
  const keyboardHandler = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (open && !event.defaultPrevented) {
          event.preventDefault();
          event.stopImmediatePropagation();
          event.stopPropagation();
          if (onClose) {
            onClose();
          }
        }
      }
    },
    [onClose, open]
  );

  // Use a proper useEffect to manage the keyup event listener, ensuring cleanup
  // runs correctly. The previous callback-ref approach returned a cleanup function,
  // but React 18 callback refs do not invoke returned cleanup functions (only React 19+
  // supports that), which caused event listeners to accumulate and never be removed.
  useEffect(() => {
    const node = nodeRef.current;
    if (!node) return;

    node.addEventListener('keyup', keyboardHandler);
    return () => {
      node.removeEventListener('keyup', keyboardHandler);
    };
  }, [keyboardHandler]);

  const setNodeRef = useCallback((node: HTMLDivElement | null) => {
    nodeRef.current = node;
  }, []);

  // Handle onClose to filter out backdrop clicks if disableBackdropClick is true
  const handleClose = useCallback(
    (_event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
      // If backdrop clicks are disabled and this is a backdrop click, don't close
      if (disableBackdropClick && reason === 'backdropClick') {
        return;
      }
      // Otherwise, call the onClose handler if it exists
      if (onClose) {
        onClose();
      }
    },
    [disableBackdropClick, onClose]
  );

  return (
    <MUIDialog
      ref={setNodeRef}
      disableEscapeKeyDown
      open={open}
      onClose={handleClose}
      {...props}
    />
  );
}
